'use client';

import { unstable_composeClasses as composeClasses } from '@mui/base/composeClasses';
import { useButton } from '@mui/base/useButton';
import ButtonGroupContext from '@mui/joy/ButtonGroup/ButtonGroupContext';
import CircularProgress from '@mui/joy/CircularProgress';
import ToggleButtonGroupContext from '@mui/joy/ToggleButtonGroup/ToggleButtonGroupContext';
import { unstable_capitalize as capitalize, unstable_useForkRef as useForkRef } from '@mui/utils';
import * as React from 'react';
import type { ButtonOwnerState, ButtonTypeMap, ExtendButton } from './ButtonBaseProps';
import { getButtonUtilityClass } from './buttonBaseClasses';

const useUtilityClasses = (ownerState: ButtonOwnerState) => {
  const {
    color,
    disabled,
    focusVisible,
    focusVisibleClassName,
    fullWidth,
    size,
    variant,
    loading,
  } = ownerState;

  const slots = {
    root: [
      'root',
      disabled && 'disabled',
      focusVisible && 'focusVisible',
      fullWidth && 'fullWidth',
      variant && `variant${capitalize(variant)}`,
      color && `color${capitalize(color)}`,
      size && `size${capitalize(size)}`,
      loading && 'loading',
    ],
    startDecorator: ['startDecorator'],
    endDecorator: ['endDecorator'],
    loadingIndicatorCenter: ['loadingIndicatorCenter'],
  };

  const composedClasses = composeClasses(slots, getButtonUtilityClass, {});

  if (focusVisible && focusVisibleClassName) {
    composedClasses.root += ` ${focusVisibleClassName}`;
  }

  return composedClasses;
};

function resolveSlotProps(
  slotPropValue: unknown,
  ownerState: ButtonOwnerState,
): Record<string, unknown> | undefined {
  return typeof slotPropValue === 'function'
    ? slotPropValue(ownerState)
    : (slotPropValue as Record<string, unknown> | undefined);
}

const ButtonBase = React.forwardRef(function ButtonBase(
  // oxlint-disable-next-line no-explicit-any -- polymorphic component accepts any props via OverridableComponent
  inProps: any,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const {
    children,
    action,
    color: colorProp = 'primary',
    variant: variantProp = 'solid',
    size: sizeProp = 'md',
    fullWidth = false,
    startDecorator,
    endDecorator,
    loading = false,
    loadingPosition = 'center',
    loadingIndicator: loadingIndicatorProp,
    disabled: disabledProp,
    component,
    slots = {},
    slotProps = {},
    className: classNameProp,
    style,
    // strip Joy-specific props from reaching the DOM
    sx: _sx,
    focusVisibleClassName: _focusVisibleClassName,
    as,
    ...other
  } = inProps;

  const buttonGroup = React.useContext(ButtonGroupContext);
  const toggleButtonGroup = React.useContext(ToggleButtonGroupContext);

  const variant = inProps.variant || buttonGroup.variant || variantProp;
  const size = inProps.size || buttonGroup.size || sizeProp;
  const color = inProps.color || buttonGroup.color || colorProp;
  const disabled =
    (inProps.loading || inProps.disabled) ?? (buttonGroup.disabled || loading || disabledProp);

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const handleRef = useForkRef(buttonRef, ref);

  const { focusVisible, setFocusVisible, getRootProps } = useButton({
    ...inProps,
    disabled,
    rootRef: handleRef,
  });

  const loadingIndicator = loadingIndicatorProp ?? (
    <CircularProgress
      color={color}
      thickness={({ xs: 2, sm: 2, md: 3, lg: 4 } as Record<string, number>)[size] || 3}
    />
  );

  React.useImperativeHandle(
    action,
    () => ({
      focusVisible: () => {
        setFocusVisible(true);
        buttonRef.current?.focus();
      },
    }),
    [setFocusVisible],
  );

  const ownerState: ButtonOwnerState = {
    ...inProps,
    color,
    fullWidth,
    variant,
    size,
    focusVisible,
    loading,
    loadingPosition,
    disabled,
  };

  const classes = useUtilityClasses(ownerState);

  const rootSlotProps = resolveSlotProps(slotProps.root, ownerState);
  const startDecoratorSlotProps = resolveSlotProps(slotProps.startDecorator, ownerState);
  const endDecoratorSlotProps = resolveSlotProps(slotProps.endDecorator, ownerState);
  const loadingCenterSlotProps = resolveSlotProps(slotProps.loadingIndicatorCenter, ownerState);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const onClick = rootSlotProps?.onClick ?? inProps.onClick;
    onClick?.(event);
    if (toggleButtonGroup) {
      toggleButtonGroup.onClick?.(event, inProps.value);
    }
  };

  let ariaPressed = rootSlotProps?.['aria-pressed'] ?? inProps['aria-pressed'];
  if (toggleButtonGroup?.value !== undefined && toggleButtonGroup?.value !== null) {
    if (Array.isArray(toggleButtonGroup.value)) {
      ariaPressed = toggleButtonGroup.value.indexOf(inProps.value) !== -1;
    } else {
      ariaPressed = toggleButtonGroup.value === inProps.value;
    }
  }

  const RootSlot = slots.root || component || as || 'button';
  const StartDecoratorSlot = slots.startDecorator || 'span';
  const EndDecoratorSlot = slots.endDecorator || 'span';
  const LoadingCenterSlot = slots.loadingIndicatorCenter || 'span';

  // merge rootSlotProps but exclude keys we handle explicitly to avoid duplication
  const {
    className: rootSlotClassName,
    style: rootSlotStyle,
    onClick: _rootOnClick,
    'aria-pressed': _rootAriaPressed,
    ...restRootSlotProps
  } = rootSlotProps ?? {};

  const rootProps = getRootProps({
    ...other,
    ...restRootSlotProps,
    onClick: handleClick,
    'aria-pressed': ariaPressed,
    className: [classes.root, classNameProp, rootSlotClassName].filter(Boolean).join(' '),
    style: { ...style, ...(rootSlotStyle as React.CSSProperties) },
  });

  return (
    <RootSlot {...rootProps}>
      {(startDecorator || (loading && loadingPosition === 'start')) && (
        <StartDecoratorSlot
          {...startDecoratorSlotProps}
          className={[classes.startDecorator, startDecoratorSlotProps?.className]
            .filter(Boolean)
            .join(' ')}
        >
          {loading && loadingPosition === 'start' ? loadingIndicator : startDecorator}
        </StartDecoratorSlot>
      )}
      {children}
      {loading && loadingPosition === 'center' && (
        <LoadingCenterSlot
          {...loadingCenterSlotProps}
          className={[classes.loadingIndicatorCenter, loadingCenterSlotProps?.className]
            .filter(Boolean)
            .join(' ')}
        >
          {loadingIndicator}
        </LoadingCenterSlot>
      )}
      {(endDecorator || (loading && loadingPosition === 'end')) && (
        <EndDecoratorSlot
          {...endDecoratorSlotProps}
          className={[classes.endDecorator, endDecoratorSlotProps?.className]
            .filter(Boolean)
            .join(' ')}
        >
          {loading && loadingPosition === 'end' ? loadingIndicator : endDecorator}
        </EndDecoratorSlot>
      )}
    </RootSlot>
  );
}) as ExtendButton<ButtonTypeMap>;

// @ts-expect-error -- internal logic for ToggleButtonGroup
ButtonBase.muiName = 'Button';

export default ButtonBase;
